import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Send, Volume2, Loader2, User, X, Save, Trash2, LogOut } from 'lucide-react';
import logo from './refluent_logo.png';

function App() {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputMode, setInputMode] = useState('text');
  const [language, setLanguage] = useState('en');
  const [error, setError] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [welcomeName, setWelcomeName] = useState('');
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const currentAudioChunksRef = useRef([]);

  // Profile State
  const [profile, setProfile] = useState({
    user_id: '',
    name: '',
    aphasia_type: '',
    spouse_name: '',
    children_names: '',
    interests: '',
    communication_style: ''
  });

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);

  // Check localStorage for existing user on mount
  useEffect(() => {
    const savedUserId = localStorage.getItem('refluent_user_id');
    if (savedUserId) {
      setProfile(prev => ({ ...prev, user_id: savedUserId }));
      setShowWelcome(false);
    }
  }, []);

  // Fetch profile when user_id is set
  useEffect(() => {
    if (profile.user_id) {
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.user_id]);

  const handleWelcomeSubmit = () => {
    const userId = welcomeName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!userId) return;
    localStorage.setItem('refluent_user_id', userId);
    setProfile(prev => ({ ...prev, user_id: userId, name: welcomeName.trim() }));
    setShowWelcome(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('refluent_user_id');
    setProfile({
      user_id: '',
      name: '',
      aphasia_type: '',
      spouse_name: '',
      children_names: '',
      interests: '',
      communication_style: ''
    });
    setInputText('');
    setOutputText('');
    setWelcomeName('');
    setShowWelcome(true);
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`/api/profile?user_id=${profile.user_id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.user_id) {
          setProfile(prev => ({ ...prev, ...data }));
        }
      }
    } catch (err) {
      console.error("Failed to load profile", err);
    }
  };

  const saveProfile = async () => {
    try {
      const response = await fetch(`/api/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      });
      if (response.ok) {
        setShowProfile(false);
        alert('Profile saved successfully!');
      } else {
        alert('Failed to save profile.');
      }
    } catch (err) {
      console.error("Failed to save profile", err);
      alert('Error saving profile.');
    }
  };

  // Handle text input submission
  const handleTextSubmit = async () => {
    if (!inputText.trim()) return;

    setIsProcessing(true);
    setOutputText('');
    setError('');

    try {
      const response = await fetch(`/api/clarify-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          user_id: profile.user_id,
          language: language
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's a profanity error
        if (data.error === 'profanity_detected') {
          setError(data.message);
        } else {
          setError('Unable to connect to the server. Make sure the backend is running on port 5000.');
        }
        return;
      }

      setOutputText(data.clarified_text || '');
    } catch (error) {
      console.error('Error processing text:', error);
      setError('Unable to connect to the server. Make sure the backend is running on port 5000.');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm("Start a new session? This will clear your recent conversation history.")) return;

    try {
      const response = await fetch(`/api/clear-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: profile.user_id })
      });

      if (response.ok) {
        alert("Session cleared! Starting fresh.");
      }
    } catch (err) {
      console.error("Error clearing history:", err);
    }
  };

  // Process audio - REAL BACKEND CALL (Scenario 2: Stop & Clarify)
  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    // Don't clear output text immediately if appending
    setError('');

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob);
      formData.append('user_id', profile.user_id);
      formData.append('language', language);

      const response = await fetch(`/api/clarify-audio`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's a profanity error
        if (data.error === 'profanity_detected') {
          // Even on error, we might want to show what was heard
          if (data.transcribed_text) {
            setInputText(data.transcribed_text);
          }
          setError(data.message);
        } else {
          setError('Error processing audio recording.');
        }
        return;
      }

      setInputText(data.transcribed_text || '');
      setOutputText(data.clarified_text || '');

    } catch (error) {
      console.error('Error processing audio:', error);
      setError('Error processing audio recording.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Cleanup AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    }
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      currentAudioChunksRef.current = []; // Reset chunks

      // Silence Detection Setup
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Silence detection check (disabled but keeps monitoring active)
      const checkSilence = () => {
        try {
          if (!analyserRef.current) {
            console.warn("⚠️ Loop stopping: analyserRef.current is null");
            return;
          }

          analyserRef.current.getByteFrequencyData(dataArray);

          // Check mediaRecorder state
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            requestAnimationFrame(checkSilence);
          } else {
            console.warn(`⚠️ Loop stopping: MediaRecorder state is ${mediaRecorderRef.current?.state}`);
          }
        } catch (err) {
          console.error("🔥 Error in checkSilence loop:", err);
        }
      };

      // Start checking loop
      requestAnimationFrame(checkSilence);

      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      // Request data every 1 second for potential live usage
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          currentAudioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Check if we used suggestions - if so, SKIP final processing


        // Final processing when STOP is clicked
        const audioBlob = new Blob(currentAudioChunksRef.current, { type: mimeType });
        await processAudio(audioBlob);
      };

      mediaRecorder.start(1000); // Capture chunks every 1s
      setIsRecording(true);
      setError('');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setError('Unable to access microphone. Please check permissions.');
    }
  };

  // Text-to-speech for output
  const speakOutput = () => {
    if ('speechSynthesis' in window && outputText) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(outputText);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      if (language === 'hi') {
        utterance.lang = 'hi-IN';
        utterance.text = outputText.replace(/[?!.,;:()""''—–\-]/g, ' ').replace(/\s+/g, ' ').trim();
        const voices = window.speechSynthesis.getVoices();
        const hindiVoice = voices.find(v => v.lang.startsWith('hi'));
        if (hindiVoice) utterance.voice = hindiVoice;
      }
      window.speechSynthesis.speak(utterance);
    }
  };

  // Handle Enter key in textarea
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  if (showWelcome) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <img src={logo} alt="Refluent Logo" className="h-32 w-32 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-indigo-900 mb-2">Project Refluent</h1>
          <p className="text-lg text-indigo-600 mb-8">Completing Speech, Connecting Lives</p>
          <label className="block text-left text-sm font-semibold text-gray-700 mb-2">
            What's your name?
          </label>
          <input
            type="text"
            value={welcomeName}
            onChange={(e) => setWelcomeName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleWelcomeSubmit()}
            className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all mb-6"
            placeholder="e.g. John"
            autoFocus
          />
          <button
            onClick={handleWelcomeSubmit}
            disabled={!welcomeName.trim()}
            className="w-full bg-indigo-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
          >
            Get Started
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 relative">
      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-indigo-50 rounded-t-2xl">
              <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2">
                <User size={28} />
                User Memory Profile
              </h2>
              <button
                onClick={() => setShowProfile(false)}
                className="p-2 hover:bg-indigo-100 rounded-full transition-colors"
              >
                <X size={24} className="text-indigo-900" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Aphasia Type/Condition</label>
                  <input
                    type="text"
                    value={profile.aphasia_type}
                    onChange={(e) => setProfile({ ...profile, aphasia_type: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                    placeholder="e.g. Broca's Aphasia"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Spouse's Name</label>
                  <input
                    type="text"
                    value={profile.spouse_name}
                    onChange={(e) => setProfile({ ...profile, spouse_name: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                    placeholder="e.g. Mary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Children's Names</label>
                  <input
                    type="text"
                    value={profile.children_names}
                    onChange={(e) => setProfile({ ...profile, children_names: e.target.value })}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                    placeholder="e.g. Tom, Sarah"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Interests & Hobbies</label>
                <textarea
                  value={profile.interests}
                  onChange={(e) => setProfile({ ...profile, interests: e.target.value })}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all h-24 resize-none"
                  placeholder="e.g. Gardening, Classic Cars, Jazz Music"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Communication Style</label>
                <textarea
                  value={profile.communication_style}
                  onChange={(e) => setProfile({ ...profile, communication_style: e.target.value })}
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all h-24 resize-none"
                  placeholder="e.g. Short sentences, formal tone, use humor"
                />
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  onClick={saveProfile}
                  className="bg-indigo-600 text-white py-3 px-8 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Save size={20} />
                  Save Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8 relative">
          <div className="absolute right-0 top-8 flex gap-2">
            <button
              onClick={clearHistory}
              className="p-3 bg-white rounded-full shadow-md text-red-500 hover:bg-red-50 transition-all"
              title="Clear Session History"
            >
              <Trash2 size={24} />
            </button>
            <button
              onClick={handleLogout}
              className="p-3 bg-white rounded-full shadow-md text-gray-500 hover:bg-gray-50 transition-all"
              title="Switch User"
            >
              <LogOut size={24} />
            </button>
            <button
              onClick={() => setShowProfile(true)}
              className="p-3 bg-white rounded-full shadow-md text-indigo-600 hover:bg-indigo-50 transition-all"
              title="User Profile"
            >
              <User size={24} />
            </button>
          </div>

          <img src={logo} alt="Clarify Logo" className="h-40 w-40 mx-auto mb-4" />
          <h1 className="text-5xl font-bold text-indigo-900 mb-3">Project Refluent</h1>
          <p className="text-xl text-indigo-700">Completing Speech, Connecting Lives</p>
        </div>
        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-6">
          {/* Input Mode Toggle */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => {
                setInputMode('text');
                setInputText('');
                setOutputText('');
                setError('');

              }}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold text-lg transition-all ${inputMode === 'text'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Text Input
            </button>
            <button
              onClick={() => {
                setInputMode('audio');
                setInputText('');
                setOutputText('');
                setError('');

              }}
              className={`flex-1 py-4 px-6 rounded-xl font-semibold text-lg transition-all ${inputMode === 'audio'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Voice Input
            </button>
          </div>

          {/* Language Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="text-sm font-medium text-gray-500">Language:</span>
            <button
              onClick={() => setLanguage('en')}
              className={`py-2 px-4 rounded-lg text-sm font-semibold transition-all ${language === 'en'
                ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
            >
              English
            </button>
            <button
              onClick={() => setLanguage('hi')}
              className={`py-2 px-4 rounded-lg text-sm font-semibold transition-all ${language === 'hi'
                ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-300'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
            >
              Hindi
            </button>
          </div>

          {/* Text Input Mode */}
          {inputMode === 'text' && (
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-700 mb-3">
                Your Message
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message here..."
                className="w-full h-40 p-4 text-lg border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
                disabled={isProcessing}
              />

              {/* Error Display - appears below input, above button */}
              {error && (
                <div className="mt-3 flex justify-center">
                  <div className="inline-block px-6 py-3 bg-red-50 border-2 border-red-200 rounded-xl">
                    <p className="text-red-700 font-semibold">{error}</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleTextSubmit}
                disabled={!inputText.trim() || isProcessing}
                className="mt-4 w-full bg-indigo-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="animate-spin" size={24} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send size={24} />
                    Rebuild Message
                  </>
                )}
              </button>
            </div>
          )}

          {/* Audio Input Mode */}
          {inputMode === 'audio' && (
            <div className="mb-6">
              <label className="block text-lg font-semibold text-gray-700 mb-3">
                Voice Recording
              </label>
              <div className="flex flex-col items-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 relative">



                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                  className={`w-32 h-32 rounded-full flex items-center justify-center transition-all shadow-xl ${isRecording
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                    } disabled:bg-gray-300 disabled:cursor-not-allowed`}
                >
                  {isRecording ? (
                    <MicOff size={48} className="text-white" />
                  ) : (
                    <Mic size={48} className="text-white" />
                  )}
                </button>
                <p className="mt-6 text-lg font-semibold text-gray-700">
                  {isRecording ? 'Recording... Tap to stop' : 'Tap to start recording'}
                </p>
                {isProcessing && (
                  <div className="mt-4 flex items-center gap-2 text-indigo-600">
                    <Loader2 className="animate-spin" size={20} />
                    <span>Processing your voice...</span>
                  </div>
                )}
              </div>

              {/* Transcription Display */}
              {inputText && (
                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Transcribed:</p>
                  <p className="text-gray-800 text-lg">{inputText}</p>
                </div>
              )}





              {/* Error Display - appears below transcription */}
              {error && (
                <div className="mt-3 flex justify-center">
                  <div className="inline-block px-6 py-3 bg-red-50 border-2 border-red-200 rounded-xl">
                    <p className="text-red-700 font-semibold">{error}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Output Section */}
          {outputText && (
            <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="flex items-center justify-between mb-3">
                <label className="text-lg font-semibold text-green-800">
                  Clarified Message
                </label>
                <button
                  onClick={speakOutput}
                  className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                  title="Listen to clarified message"
                >
                  <Volume2 size={20} />
                </button>
              </div>
              <p className="text-xl text-gray-800 leading-relaxed">{outputText}</p>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <p className="text-gray-600">
            Project Refluent - An AI powered app to help you rebuild your speech, restore your connection, and regain your confidence.

          </p>
        </div>
      </div>
    </div>
  );
}

export default App;